// Customer-facing ticket detail. Shows non-internal comments + lets the
// customer reply. No status/priority editing — that's agent-only.
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { usePortal } from './PortalLayout';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import ServiceDeskAttachments from '../../components/serviceDesk/ServiceDeskAttachments';
import CsatWidget from '../../components/serviceDesk/CsatWidget';
import TicketFields from '../../components/serviceDesk/TicketFields';
import RichEditor, { RichEditorContent } from '../../components/RichEditor';
import { StatusBadge } from '../../components/serviceDesk/ticketMeta';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function PortalTicketDetailPage() {
  const { slug, ticketKey } = useParams();
  const navigate = useNavigate();
  const { customer, customerChecked } = usePortal();
  const [ticket, setTicket] = useState(null);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/customer/tickets/${ticketKey}`, { withCredentials: true });
      setTicket(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [ticketKey]);

  useEffect(() => {
    if (!customerChecked) return; // wait until the session check completes
    if (!customer) { navigate(`/help/${slug}/login`); return; }
    load();
  }, [customer, customerChecked, slug, navigate, load]);

  const reply = async () => {
    if (!body.trim()) return;
    try {
      await axios.post(`${API}/customer/tickets/${ticketKey}/comments`, { body: body.trim() }, { withCredentials: true });
      setBody('');
      toast.success('Reply sent');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const doReopen = async () => {
    setReopening(true);
    try {
      const { data } = await axios.post(`${API}/customer/tickets/${ticketKey}/reopen`,
        { reason: reopenReason.trim() }, { withCredentials: true });
      setTicket(data);
      setShowReopen(false);
      setReopenReason('');
      toast.success('Request reopened');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reopen');
    } finally { setReopening(false); }
  };

  if (loading) return <p className="text-zinc-400 text-sm">Loading…</p>;
  if (!ticket) return null;

  const hasForm = (ticket.request_type?.form_fields?.length > 0) && ticket.form_data && Object.keys(ticket.form_data).length > 0;
  const cinitials = (n) => (n || '?').trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?';
  const editable = !['resolved', 'closed'].includes(ticket.status);

  const saveField = async (key, value) => {
    try {
      const { data } = await axios.patch(`${API}/customer/tickets/${ticketKey}`,
        { form_data: { [key]: value } }, { withCredentials: true });
      setTicket(data);
      toast.success('Updated');
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
      return false;
    }
  };

  const uploadCommentImage = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/public/portal/${slug}/tickets/${ticketKey}/comment-images`, fd, { withCredentials: true });
      return { url: data.url };
    } catch (err) {
      return { error: err?.response?.data?.detail || 'Upload failed' };
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="portal-ticket-detail">
      <Link to={`/help/${slug}/requests`} className="text-xs text-zinc-500 hover:text-zinc-900">← Back to my requests</Link>
      <div className="bg-white border border-zinc-200 rounded-lg p-6 mt-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-xs font-bold text-blue-700">{ticket.ticket_key}</span>
          <StatusBadge status={ticket.status} />
          {ticket.request_type?.name && <span className="text-[11px] text-zinc-400">· {ticket.request_type.name}</span>}
        </div>
        <h1 className="text-xl font-bold text-zinc-900 font-['Outfit']">{ticket.title}</h1>
        {hasForm ? (
          <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="portal-ticket-fields">
            <h2 className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Request details</h2>
            <TicketFields fields={ticket.request_type?.form_fields} formData={ticket.form_data} editable={editable} onSaveField={saveField} />
          </div>
        ) : (
          ticket.description && <p className="text-sm text-zinc-700 whitespace-pre-wrap mt-3">{ticket.description}</p>
        )}
        <p className="text-[11px] text-zinc-500 mt-3">Created {new Date(ticket.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
      </div>

      {/* CSAT — ask the customer to rate once their request is resolved/closed */}
      {['resolved', 'closed'].includes(ticket.status) && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 mt-4" data-testid="portal-reopen-card">
          <h2 className="text-sm font-semibold text-zinc-900 mb-1">Not fully resolved?</h2>
          <p className="text-xs text-zinc-500 mb-3">If your issue isn't solved, reopen this request and we'll pick it back up.</p>
          {!showReopen ? (
            <Button size="sm" variant="outline" onClick={() => setShowReopen(true)} data-testid="portal-reopen-btn">
              Reopen request
            </Button>
          ) : (
            <div className="space-y-2">
              <Textarea rows={3} value={reopenReason} onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Optionally, tell us what's still wrong…" data-testid="portal-reopen-reason" />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => { setShowReopen(false); setReopenReason(''); }}>Cancel</Button>
                <Button size="sm" onClick={doReopen} disabled={reopening} data-testid="portal-reopen-submit">
                  {reopening ? 'Reopening…' : 'Reopen request'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CSAT — ask the customer to rate once their request is resolved/closed */}
      {['resolved', 'closed'].includes(ticket.status) && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 mt-4" data-testid="portal-csat-card">
          <CsatWidget apiBase={API} ticketKey={ticket.ticket_key} csat={ticket.csat}
            onRated={(csat) => setTicket(t => ({ ...t, csat }))} />
        </div>
      )}

      {/* Customer-side attachments — internal ones are filtered out by the API */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 mt-4">
        <h2 className="text-sm font-semibold text-zinc-900 mb-3">Attachments</h2>
        <ServiceDeskAttachments
          listUrl={`${API}/customer/tickets/${ticket.ticket_key}/attachments`}
          uploadUrl={`${API}/public/portal/${slug}/tickets/${ticket.ticket_key}/attachments`}
          withCredentials={true}
          readOnly={['resolved', 'closed'].includes(ticket.status)}
        />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6 mt-4">
        <h2 className="text-sm font-semibold text-zinc-900 mb-3">Conversation</h2>
        {(ticket.comments || []).length === 0 ? (
          <p className="text-xs text-zinc-400">No replies yet.</p>
        ) : (
          <div className="space-y-4">
            {ticket.comments.map(c => {
              const mine = !!c.author_customer_id;
              const name = mine ? (customer?.name || 'You') : 'Support team';
              return (
                <div key={c.comment_id} className="flex gap-3" data-testid={`portal-comment-${c.comment_id}`}>
                  <div className={`w-8 h-8 rounded-full text-white text-[11px] font-bold flex items-center justify-center shrink-0 ${mine ? 'bg-zinc-400' : 'bg-blue-500'}`}>
                    {cinitials(name)}
                  </div>
                  <div className="flex-1 border border-zinc-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <span className="text-sm font-semibold text-zinc-900">{mine ? 'You' : 'Support team'}</span>
                      <span className="text-[11px] text-zinc-500 shrink-0">{new Date(c.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                    </div>
                    <div className="text-sm text-zinc-800 break-words"><RichEditorContent html={c.body} /></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!['resolved', 'closed'].includes(ticket.status) && (
          <div className="mt-4 border-t border-zinc-100 pt-3">
            <div data-testid="portal-reply-input">
              <RichEditor value={body} onChange={setBody} uploadImage={uploadCommentImage} minHeight={80}
                placeholder="Add a reply… paste screenshots directly" />
            </div>
            <div className="mt-2 flex justify-end">
              <Button size="sm" onClick={reply} disabled={!body.trim()} data-testid="portal-reply-submit">Send reply</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
